import { Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { ProduktDashboardReq } from '../intern/model/intern-backend.model'

/* Model */

export interface ProduktDashboardStateModel {
  version: number
  produktColumns: {
    keys: string[]
    visible: string[]
  }
  produktFilter: ProduktDashboardReq
}

export const produktAvailableColumns: { label: string; key: string; defaultVisible?: boolean }[] = [
  { label: 'Bild', key: 'image', defaultVisible: true },
  { label: 'Name', key: 'name', defaultVisible: true },
  { label: 'PZN', key: 'pzn', defaultVisible: true },
  { label: 'EAN', key: 'ean', defaultVisible: true },
  { label: 'Fallback-ID', key: 'fallbackIdentifier', defaultVisible: true },
  { label: 'Interne Matching-ID', key: 'internalMatchingId', defaultVisible: true },
  { label: 'Hilfsmittelnummer', key: 'hilfsmittelnummer', defaultVisible: true },
  { label: 'Hersteller', key: 'manufacturer', defaultVisible: true },
  { label: 'Verpackungseinheit', key: 'packagingUnit', defaultVisible: true },
  { label: 'Beschreibung', key: 'description' },
  // „SaniUp-EK“ statt „Einkaufspreise“: seit es die Konditionen eines Leistungserbringers als eigene
  // Spalten gibt, stuenden sonst zwei Einkaufspreise nebeneinander, die nichts miteinander zu tun haben.
  { label: 'SaniUp-EK', key: 'purchasePrices' },
]

export const produktColumnNameMap = produktAvailableColumns.reduce(
  (acc, col) => {
    acc[col.key] = col.label
    return acc
  },
  {} as Record<string, string>,
)

/* Actions */

export class ProduktDashboardUpdateColumns {
  static readonly type = '[ProduktDashboard] Update Columns'
}

export class ProduktDashboardMoveColumns {
  static readonly type = '[ProduktDashboard] Move Columns'
  constructor(
    public indexA: number,
    public indexB: number,
  ) {}
}

export class ProduktDashboardSetColumnVisible {
  static readonly type = '[ProduktDashboard] Set Column Visible'
  constructor(public keys: string[]) {}
}

export class ProduktDashboardReset {
  static readonly type = '[ProduktDashboard] Reset Columns'
}

export class ProduktDashboardSetFilter {
  static readonly type = '[ProduktDashboard] Set Filter'
  constructor(public filter: Partial<ProduktDashboardReq>) {}
}

export class ProduktDashboardResetFilter {
  static readonly type = '[ProduktDashboard] Reset Filter'
}

/* State */

const defaultState: ProduktDashboardStateModel = {
  // Bei Änderungen an den verfügbaren Spalten hochzählen, damit persistierte
  // Konfigurationen über ProduktDashboardUpdateColumns migriert werden.
  version: 3,

  produktColumns: {
    keys: produktAvailableColumns.map((c) => c.key),
    visible: produktAvailableColumns.filter((c) => c.defaultVisible).map((c) => c.key),
  },
  produktFilter: {
    pageSize: 30,
  },
}

@State<ProduktDashboardStateModel>({
  name: 'produktDashboard',
  defaults: defaultState,
})
@Injectable()
export class ProduktDashboardState {
  @Selector()
  static produktAvailableColumns(state: ProduktDashboardStateModel) {
    return state.produktColumns.keys
  }

  @Selector()
  static produktVisibleColumns(state: ProduktDashboardStateModel) {
    return state.produktColumns.visible
  }

  @Selector()
  static produktFilter(state: ProduktDashboardStateModel) {
    return state.produktFilter
  }

  @Action(ProduktDashboardUpdateColumns)
  updateColumns(ctx: StateContext<ProduktDashboardStateModel>) {
    const { version } = ctx.getState()
    if (version !== defaultState.version) {
      // Version-Sprung: Spalten-Konfiguration auf die neuen Defaults zurücksetzen
      // (der persistierte Filter bleibt erhalten).
      ctx.patchState({
        version: defaultState.version,
        produktColumns: defaultState.produktColumns,
      })
      return
    }

    const columns = ctx.getState().produktColumns.keys.slice()
    const allColumnKeys = produktAvailableColumns.map((c) => c.key)

    allColumnKeys.forEach((key) => {
      if (!columns.includes(key)) {
        columns.push(key)
      }
    })
    const filteredColumns = columns.filter((key) => allColumnKeys.includes(key))

    ctx.patchState({
      produktColumns: {
        ...ctx.getState().produktColumns,
        keys: filteredColumns,
      },
    })
  }

  @Action(ProduktDashboardMoveColumns)
  moveColumns(ctx: StateContext<ProduktDashboardStateModel>, action: ProduktDashboardMoveColumns) {
    const columns = ctx.getState().produktColumns.keys.slice()
    const [movedColumn] = columns.splice(action.indexA, 1)
    columns.splice(action.indexB, 0, movedColumn)
    ctx.patchState({
      produktColumns: {
        ...ctx.getState().produktColumns,
        keys: columns,
      },
    })
  }

  @Action(ProduktDashboardSetColumnVisible)
  setColumnVisible(ctx: StateContext<ProduktDashboardStateModel>, action: ProduktDashboardSetColumnVisible) {
    ctx.patchState({
      produktColumns: {
        ...ctx.getState().produktColumns,
        visible: action.keys,
      },
    })
  }

  @Action(ProduktDashboardReset)
  resetColumns(ctx: StateContext<ProduktDashboardStateModel>) {
    ctx.patchState({
      produktColumns: defaultState.produktColumns,
    })
  }

  @Action(ProduktDashboardSetFilter)
  setFilter(ctx: StateContext<ProduktDashboardStateModel>, action: ProduktDashboardSetFilter) {
    ctx.patchState({
      produktFilter: {
        ...ctx.getState().produktFilter,
        ...action.filter,
      },
    })
  }

  @Action(ProduktDashboardResetFilter)
  resetFilter(ctx: StateContext<ProduktDashboardStateModel>) {
    ctx.patchState({
      produktFilter: defaultState.produktFilter,
    })
  }
}
